import React from 'react';

import type { IconRowProps } from './IconRow.types';

/**
 * IconRow - a glyph and a line of explanation, hanging-indented.
 *
 * The shape of "here is how this works": a dimmed icon in the left gutter and
 * prose beside it that wraps under itself rather than under the icon. Both
 * Goals cards had defined this privately as `DossierRow`, differing only in
 * `leading-relaxed` and in `flex-none` versus `shrink-0` — neither a decision
 * anyone made twice.
 *
 * It is deliberately not a `FactList` item: a fact is a label and a value in
 * tension across the row, and this is one sentence with a marker.
 *
 * @example
 * <IconRow icon={<BoltIcon className="h-3.5 w-3.5" />}>
 *   Every working set you log updates your estimated 1RM.
 * </IconRow>
 */
export function IconRow({
  icon,
  children,
  className = '',
  'data-testid': testId,
}: IconRowProps): React.JSX.Element {
  return (
    <div
      className={`flex items-start gap-2 text-body-sm leading-relaxed text-neutral-400 ${className}`.trim()}
      data-testid={testId}
    >
      <span className="mt-0.5 flex-none text-neutral-600" aria-hidden="true">
        {icon}
      </span>
      <span>{children}</span>
    </div>
  );
}

export default IconRow;
